import React, { useState } from 'react';
import { Link, useLocation, useHistory, useParams } from 'react-router-dom';
import AdminShell from './AdminShell';
import EmployeesTab from './tabs/EmployeesTab';
import DashboardTab from './tabs/DashboardTab';
import ResultsTab from './tabs/ResultsTab';
import ConsultantTab from './tabs/ConsultantTab';
import SendInvitationsDialog from './dialogs/SendInvitationsDialog';
import SendRemindersDialog from './dialogs/SendRemindersDialog';
import CloseCampaignDialog from './dialogs/CloseCampaignDialog';
import ImportEmployeesDialog from './dialogs/ImportEmployeesDialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { CAMPAIGNS, statusVariant, prettyDate } from '../fixtures';
import { ArrowLeft, Download, Mail, Send, Lock, FileDown, Eye, RotateCcw } from 'lucide-react';

const CampaignDetail = () => {
  const { id } = useParams();
  const { search } = useLocation();
  const history = useHistory();
  const params = new URLSearchParams(search);
  const tab = params.get('tab') || 'employees';

  const campaign = CAMPAIGNS.find((c) => c.id === id) || CAMPAIGNS[0];

  const [importOpen, setImportOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [remindOpen, setRemindOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);

  const setTab = (next) => {
    const nextParams = new URLSearchParams(search);
    nextParams.set('tab', next);
    history.push({ pathname: history.location.pathname, search: `?${nextParams.toString()}` });
  };

  const actionsByStatus = {
    draft: (
      <>
        <Button variant='outline' onClick={() => setImportOpen(true)}>
          Import employees
        </Button>
        <Button onClick={() => setSendOpen(true)} disabled>
          <Send size={14} strokeWidth={1.5} />
          Send invitations
        </Button>
      </>
    ),
    active: (
      <>
        <Button variant='outline' onClick={() => setRemindOpen(true)}>
          <Mail size={14} strokeWidth={1.5} />
          Send reminders
        </Button>
        <Button onClick={() => setCloseOpen(true)}>
          <Lock size={14} strokeWidth={1.5} />
          Close campaign
        </Button>
      </>
    ),
    closing: (
      <Button variant='outline' disabled loading>
        Calculating results...
      </Button>
    ),
    closed: (
      <>
        <Button variant='outline'>
          <RotateCcw size={14} strokeWidth={1.5} />
          Re-open
        </Button>
        <Button variant='outline'>
          <FileDown size={14} strokeWidth={1.5} />
          Export road-travel CSV
        </Button>
        <Button>
          <Download size={14} strokeWidth={1.5} />
          Export responses
        </Button>
      </>
    ),
  };

  return (
    <AdminShell
      breadcrumbs={[
        { label: 'Surveys', to: '/prototype/wfh-survey/admin/list' },
        { label: campaign.name },
      ]}
      eyebrow={`${campaign.reportingPeriod} campaign`}
      title={campaign.name}
      description={
        <span className='inline-flex items-center gap-3 flex-wrap'>
          <Badge
            variant={statusVariant(campaign.status)}
            size='default'
            dot={campaign.status === 'active' || campaign.status === 'closing'}>
            {campaign.statusLabel}
          </Badge>
          <span className='text-sm text-muted-foreground'>Deadline {prettyDate(campaign.deadline)}</span>
          {campaign.status === 'closed' && (
            <span className='text-sm text-muted-foreground'>· Closed {prettyDate(campaign.closedAt)}</span>
          )}
        </span>
      }
      actions={
        <>
          <Button variant='ghost' asChild>
            <Link to='/prototype/wfh-survey/admin/list'>
              <ArrowLeft size={14} strokeWidth={1.5} />
              All campaigns
            </Link>
          </Button>
          {actionsByStatus[campaign.status]}
        </>
      }>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value='employees'>Employees</TabsTrigger>
          <TabsTrigger value='dashboard'>Dashboard</TabsTrigger>
          <TabsTrigger value='results' disabled={campaign.status === 'draft'}>
            Results
          </TabsTrigger>
          <TabsTrigger value='consultant'>
            <Eye size={14} strokeWidth={1.5} />
            Consultant
          </TabsTrigger>
        </TabsList>

        <TabsContent value='employees'>
          <EmployeesTab campaign={campaign} onImportClick={() => setImportOpen(true)} />
        </TabsContent>

        <TabsContent value='dashboard'>
          <DashboardTab campaign={campaign} onRemindClick={() => setRemindOpen(true)} />
        </TabsContent>

        <TabsContent value='results'>
          <ResultsTab campaign={campaign} />
        </TabsContent>

        <TabsContent value='consultant'>
          <ConsultantTab campaign={campaign} />
        </TabsContent>
      </Tabs>

      <ImportEmployeesDialog open={importOpen} onOpenChange={setImportOpen} />
      <SendInvitationsDialog open={sendOpen} onOpenChange={setSendOpen} campaign={campaign} />
      <SendRemindersDialog open={remindOpen} onOpenChange={setRemindOpen} campaign={campaign} />
      <CloseCampaignDialog open={closeOpen} onOpenChange={setCloseOpen} campaign={campaign} />
    </AdminShell>
  );
};

export default CampaignDetail;
